/**
 * Reads the GA4 identity baton (`local_uuid`) from browser storage.
 *
 * The `local_uuid` is a long-lived UUID provisioned by aapm-web's
 * `generateUUIDIdentifier` helper (utils/cookies/uuidIdentifier.ts) and
 * stored in BOTH localStorage and a 10-year cookie. It threads a
 * consistent identity across pre-auth and post-auth sessions so
 * server-side GA4 events can stitch cross-device journeys back to one
 * Person row.
 *
 * Used by the resident-portal OTP-login Person binding (CLAUDE-16). If
 * the resident lands on the portal cold (no prior aapm-web visit), this
 * returns undefined and `bindPersonToUser` no-ops — the binding will
 * happen on a later session once the cookie is provisioned, or via the
 * sfr3-common GA4 baton work that ships the same UUID across all
 * resident-facing properties.
 */
export function getLocalUuid(): string | undefined {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const fromStorage = window.localStorage.getItem('local_uuid');
    if (fromStorage) {
      return fromStorage;
    }
  } catch {
    // localStorage can throw in privacy/incognito modes or when
    // disabled — fall through to the cookie path.
  }

  if (typeof document === 'undefined') {
    return;
  }

  const cookieRow = document.cookie
    .split('; ')
    .find((row) => row.startsWith('local_uuid='));
  if (!cookieRow) {
    return;
  }

  const value = cookieRow.slice('local_uuid='.length);
  return value || undefined;
}
